import { Medal } from 'lucide-react';
import { MEDALHAS, ORDEM_MEDALHAS, medalhasVisiveis, podio } from '../../lib/medalhas';

/**
 * ETAPA VISUAL 5 — MEDALHAS E PÓDIO
 *
 * Duas peças que existiam DUPLICADAS e com paletas diferentes:
 *
 *   `FederativoRankingPage.MedalCounts`  -> ícone + número (paleta Tailwind)
 *   `MedalsPage.SummaryCard`             -> ícone + rótulo + número (Tailwind)
 *   `PublicRankingPage.placeBadge`       -> pílula de posição (paleta e-Torneios)
 *   `FederativoRankingPage.PositionBadge`-> pílula de posição (dourado próprio)
 *
 * Aqui viram duas peças só, lendo a mesma paleta de `lib/medalhas.js`.
 */

/**
 * Contagem de medalhas de uma linha: ícone colorido + número.
 *
 * `compacto` é para linha de tabela (ícones de 14px, gaps curtos);
 * `grande` é para o resumo/cabeçalho da tela.
 * Só mostra as medalhas com valor > 0 — coluna com "🥈 0" é ruído.
 */
export function ContagemMedalhas({ medals, incluirSegundoBronze = false, tamanho = 'compacto' }) {
  const lista = medalhasVisiveis(medals, incluirSegundoBronze);
  const icone = tamanho === 'grande' ? 16 : 14;
  const texto = tamanho === 'grande' ? 'text-sm' : 'text-xs';

  if (lista.length === 0) {
    return <span className="text-[#71717A] text-xs">—</span>;
  }

  return (
    <div className={`flex items-center ${tamanho === 'grande' ? 'gap-4' : 'gap-3'} ${texto}`} data-testid="contagem-medalhas">
      {lista.map((m) => (
        <span key={m.chave} className={`flex items-center gap-1 ${m.classe}`} title={m.rotulo}>
          <Medal size={icone} aria-hidden="true" />
          <span className="text-white">{m.valor}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Resumo no formato "rótulo + número", para o cabeçalho de uma tela de medalhas.
 */
export function ResumoMedalhas({ medals, incluirSegundoBronze = false }) {
  const c = medals || {};
  const chaves = incluirSegundoBronze ? ORDEM_MEDALHAS : ORDEM_MEDALHAS.slice(0, 3);
  return (
    <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="resumo-medalhas">
      {chaves.map((chave) => {
        const def = MEDALHAS[chave];
        return (
          <span key={chave} className="flex items-center gap-2">
            <Medal size={16} aria-hidden="true" className={def.classe} />
            <span className="text-[#A1A1AA]">{def.rotulo}</span>
            <span className="font-bold text-white">{Number(c[def.campo]) || 0}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Selo de posição (pódio). Fundo colorido + texto preto: contraste garantido nos
 * dois temas sem variante clara. Fora do pódio é neutro.
 */
export function SeloPosicao({ posicao, className = '' }) {
  const { classe, rotulo } = podio(posicao);
  return (
    <span
      className={`inline-flex min-w-[34px] items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${classe} ${className}`}
      data-testid={`selo-posicao-${posicao}`}
    >
      {rotulo}
    </span>
  );
}
